import * as React from 'react';
import { useLayoutEffect } from 'react';
import { DocumentMimeKind } from '../types';
import { OcrOverlayBox } from '../ocr/use-ocr';

export function ViewerStage(props: {
    containerRef: React.RefObject<HTMLDivElement>;
    canvasRef: React.RefObject<HTMLCanvasElement>;
    textLayerRef: React.RefObject<HTMLDivElement>;
    imageRef: React.RefObject<HTMLImageElement>;
    loading: boolean;
    aligning: boolean;
    error: string | null;
    mimeKind: DocumentMimeKind;
    displayUrl: string;
    verifyLabel?: string;
    pan: { x: number; y: number };
    rotateTurns: number;
    scale: number;
    fitScale: number;
    imageNatural: { w: number; h: number };
    setImageNatural: (s: { w: number; h: number }) => void;
    fitImageToStage: () => void;
    canPan: boolean;
    overlayBoxes: OcrOverlayBox[];
    pendingFocusRef: React.MutableRefObject<boolean>;
    panHitIntoView: (els: ArrayLike<Element>) => boolean;
    onPointerDown: (e: React.PointerEvent) => void;
    onPointerMove: (e: React.PointerEvent) => void;
    onPointerUp: () => void;
    onStageDoubleClick: (e: React.MouseEvent) => void;
}) {
    useLayoutEffect(() => {
        if (!props.pendingFocusRef.current) {
            return;
        }

        const root = props.containerRef.current;

        if (!root) {
            return;
        }

        const nodes = root.querySelectorAll('.dvOcrHitActive');

        if (!nodes.length) {
            return;
        }

        const id = requestAnimationFrame(() => {
            if (!props.pendingFocusRef.current) {
                return;
            }

            if (props.panHitIntoView(nodes)) {
                props.pendingFocusRef.current = false;
            }
        });

        return () => cancelAnimationFrame(id);
    }, [props.overlayBoxes, props.mimeKind, props.containerRef, props.pendingFocusRef, props.panHitIntoView]);

    const overlay = props.overlayBoxes.length > 0 && (
        <div className="dvOcrOverlay" aria-hidden="true">
            {props.overlayBoxes.map(box => (
                <div
                    key={box.key}
                    className={box.active ? 'dvOcrHit dvOcrHitActive' : (box.soft ? 'dvOcrHitSoft' : 'dvOcrHit')}
                    style={{
                        left: box.left + '%',
                        top: box.top + '%',
                        width: box.width + '%',
                        height: box.height + '%'
                    }}
                />
            ))}
        </div>
    );

    return (
        <div
            ref={props.containerRef}
            className={['dvStage', props.canPan ? 'dvStagePannable' : ''].filter(Boolean).join(' ')}
            tabIndex={0}
            onPointerDown={props.onPointerDown}
            onPointerMove={props.onPointerMove}
            onPointerUp={props.onPointerUp}
            onPointerCancel={props.onPointerUp}
            onDoubleClick={props.onStageDoubleClick}
            style={{
                cursor: props.canPan ? 'grab' : 'zoom-in',
                position: 'relative',
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                overflow: 'hidden',
                outline: 'none',
                touchAction: 'none'
            }}
        >
            {props.loading && <div className="dvStageMsg">Loading file…</div>}
            {!props.loading && props.aligning && <div className="dvStageMsg">Aligning for best fit…</div>}
            {!props.loading && props.error && <div className="dvStageMsg dvStatus">{props.error}</div>}
            {!props.loading && !props.error && props.mimeKind === 'pdf' && (
                <div
                    style={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: `translate(-50%, -50%) translate(${props.pan.x}px, ${props.pan.y}px) rotate(${props.rotateTurns * 90}deg)`,
                        margin: 0
                    }}
                >
                    <div
                        className="pdfPage"
                        style={{ position: 'relative' }}
                    >
                        <canvas ref={props.canvasRef} />
                        <div ref={props.textLayerRef} className="textLayer" />
                        {overlay}
                    </div>
                </div>
            )}
            {!props.loading && !props.error && !props.aligning && props.mimeKind === 'image' && props.displayUrl && (
                <div
                    style={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: `translate(-50%, -50%) translate(${props.pan.x}px, ${props.pan.y}px) rotate(${props.rotateTurns * 90}deg)`,
                        margin: 0
                    }}
                >
                    <div className="dvImageWrap">
                        <img
                            ref={props.imageRef}
                            src={props.displayUrl}
                            alt={props.verifyLabel || 'Document'}
                            draggable={false}
                            onLoad={e => {
                                const el = e.currentTarget;
                                props.setImageNatural({
                                    w: el.naturalWidth || 0,
                                    h: el.naturalHeight || 0
                                });
                                props.fitImageToStage();
                            }}
                            style={{
                                width: props.imageNatural.w > 0
                                    ? `${Math.max(1, props.imageNatural.w * props.fitScale * props.scale)}px`
                                    : 'auto',
                                height: 'auto',
                                maxWidth: 'none',
                                maxHeight: 'none',
                                objectFit: 'contain',
                                display: 'block'
                            }}
                        />
                        {overlay}
                    </div>
                </div>
            )}
        </div>
    );
}
